import { runInAction } from 'mobx';
import { Button, Grid, GridColumn, Heading, IFormHelperProps, Modal, ModalContent, Notice, Separator, Text, TextField } from '@teliaee/design-system';
import { observer } from 'mobx-react';
import React from 'react';
import HiddenCaptcha, { HiddenCaptchaRef } from '../components/HiddenCaptcha';
import useStores from '../stores/UseStores';
import { isEmailValid } from '../util/ValidationUtil';
import { EMAIL_FIELD_ID, emailFieldHelper, getErroredHelperId, getHelperErrorMessage, helperInErrorState } from '../util/FieldHelperUtil';
import UiNotice from '../components/UiNotice';
import { useDidUpdate } from '../util/hooks/useDidUpdate';

const AddEmailModal = observer((): React.JSX.Element => {
  const {
    accountInfoStore: { emails: emailAccounts, isDealerSession },
    addEmailStore: { addEmailModalOpen, fetching, email, response, setAddEmailModalState, handleSubmit, checkSessionValid, setEmail, setResponse },
    translateStore: { translate },
    uiStore: { showErrorNoticeWithErroredFields },
    languageStore: { currentLanguage },
  } = useStores();
  const [hasSubmitted, setHasSubmitted] = React.useState<boolean>(false);
  const recaptchaRef = React.createRef<HiddenCaptchaRef>();

  useDidUpdate(() => {
    if (addEmailModalOpen) {
      runInAction(() => {
        setEmail('');
      });
      setHasSubmitted(false);
      checkSessionValid();
    }
  }, [addEmailModalOpen]);

  const setEmailInStore = React.useCallback((email: string) => {
    setEmail(email);
  }, []);

  const handleFormSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setResponse(null);
    setHasSubmitted(true);
    if (email && !isInvalid()) {
      const token = await recaptchaRef?.current?.getCaptchaAsync();
      handleSubmit(token);
    } else {
      showErrorNoticeWithErroredFields('form.error', [emailFormHelper(true) || getEmailResponseErrorHelper()]);
    }
  };

  const getEmailResponseErrorHelper = (): IFormHelperProps | undefined => {
    if (response?.emailError) {
      return { type: 'error', text: translate(response.emailError), id: getErroredHelperId(EMAIL_FIELD_ID) };
    }
    if (emailAccounts?.find((emailAccount) => emailAccount.address.toLocaleLowerCase() === email.toLocaleLowerCase())) {
      return { type: 'error', text: translate('addemail.error.already.added'), id: getErroredHelperId(EMAIL_FIELD_ID) };
    }
    return undefined;
  };

  const isInvalid = (): boolean => {
    return !isEmailValid(email) || !!getEmailResponseErrorHelper();
  };

  const emailFormHelper = (forceIsDirty?: boolean) => emailFieldHelper(translate, email, forceIsDirty ?? hasSubmitted);

  return (
    <Modal
      isOpen={addEmailModalOpen}
      size="6-col"
      aria-labelledby="add-new-email-modal-title"
      onClose={() => setAddEmailModalState(false)}
      initialFocusedElementId="add-new-email-email"
    >
      <ModalContent>
        <UiNotice only={['form.error']} />
        <form onSubmit={handleFormSubmit} id="emailLogin">
          <Grid align={['center-xs']} className="text-center">
            <GridColumn width={['xs-12', 'sm-10', 'md-8', 'lg-10']}>
              <Text>
                <Heading level={1} className="h3 text-center" id="add-new-email-modal-title">
                  {translate('add_email.header')}
                </Heading>
                <Separator spacing="xsmall" color="invisible" />
                {isDealerSession && (
                  <>
                    <Notice type="error">{translate('add_email.dealer-notice')}</Notice>
                    <Separator spacing="xsmall" color="invisible" />
                  </>
                )}
                <p>{translate('email_register.email_title')}</p>
                <TextField
                  label={translate('email_register.email_label')}
                  id={EMAIL_FIELD_ID}
                  value={email}
                  isDisabled={isDealerSession}
                  onChange={setEmailInStore}
                  isInvalid={hasSubmitted && (helperInErrorState(emailFormHelper()) || !!getEmailResponseErrorHelper())}
                  errorMessage={getHelperErrorMessage(emailFormHelper()) || getHelperErrorMessage(getEmailResponseErrorHelper())}
                  required={true}
                  input={{ autoComplete: 'email' }}
                />
                <HiddenCaptcha ref={recaptchaRef} currentLanguage={currentLanguage} />
                <Separator color="invisible" spacing="small" />
                <Button
                  text={translate('email_register.email_button')}
                  iconRight="#arrow-right"
                  onClick={handleFormSubmit}
                  isLoading={fetching}
                  disabled={fetching || isDealerSession}
                />
              </Text>
            </GridColumn>
          </Grid>
        </form>
      </ModalContent>
    </Modal>
  );
});

export default AddEmailModal;
